/**
 * 2D visibility polygon (light occlusion).
 * Кидаємо промені з джерела до кінців усіх стін-сегментів (+/- ε),
 * беремо найближчий перетин — отримуємо полігон освітленої зони.
 * Сегмент = [ax, ay, bx, by]. Повертає список точок полігона у порядку кута.
 */
export type Seg = [number, number, number, number];

function rayHit(ox: number, oy: number, dx: number, dy: number, ax: number, ay: number, bx: number, by: number): number | null {
  const sx = bx - ax, sy = by - ay;
  const denom = dx * sy - dy * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((ax - ox) * sy - (ay - oy) * sx) / denom;
  const u = ((ax - ox) * dy - (ay - oy) * dx) / denom;
  if (t >= 0 && u >= -1e-6 && u <= 1 + 1e-6) return t;
  return null;
}

export function visibilityPolygon(cx: number, cy: number, segs: Seg[], maxR: number): Array<[number, number]> {
  const angles: number[] = [];
  for (const s of segs) {
    const ends: Array<[number, number]> = [[s[0], s[1]], [s[2], s[3]]];
    for (const p of ends) {
      const a = Math.atan2(p[1] - cy, p[0] - cx);
      angles.push(a - 0.0002, a, a + 0.0002);
    }
  }
  const hits: Array<[number, number, number]> = [];
  for (const a of angles) {
    const dx = Math.cos(a), dy = Math.sin(a);
    let best = maxR;
    for (const s of segs) { const t = rayHit(cx, cy, dx, dy, s[0], s[1], s[2], s[3]); if (t !== null && t < best) best = t; }
    hits.push([a, cx + dx * best, cy + dy * best]);
  }
  hits.sort((p, q) => p[0] - q[0]);
  return hits.map((h) => [h[1], h[2]]);
}

/** Сегменти стін + рамка мапи (0..w × 0..h). */
export function wallSegments(walls: Array<{ pts: number[][] }>, w: number, h: number = w): Seg[] {
  const segs: Seg[] = [[0, 0, w, 0], [w, 0, w, h], [w, h, 0, h], [0, h, 0, 0]];
  for (const w of walls) {
    for (let i = 0; i < w.pts.length - 1; i++) {
      const a = w.pts[i]!, b = w.pts[i + 1]!;
      segs.push([a[0]!, a[1]!, b[0]!, b[1]!]);
    }
  }
  return segs;
}
